import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const toEdit = (m) => m.map((r) => r.map((v) => v ?? ""));
const put = (m, o, q, val) => m.map((r, i) => (i === o ? r.map((x, j) => (j === q ? val : x)) : r));
const toNum = (m) => m.map((r) => r.map((v) => (v === "" || v === null ? null : Number(v))));
export const emptyTable = () => ({ option_label: "Cores", options: [], quantities: [], prices: [], reseller_prices: [] });
export const editableTable = (t) => (t ? { ...t, prices: toEdit(t.prices), reseller_prices: toEdit(t.reseller_prices || t.prices.map((r) => r.map(() => null))) } : null);
export const tablePayload = (t) => {
  const reseller = toNum(t.reseller_prices);
  return { ...t, option_label: t.option_label.trim() || "Opção", prices: toNum(t.prices), reseller_prices: reseller.flat().some((v) => v > 0) ? reseller : null };
};

const Adder = ({ id, label, value, setValue, onAdd, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold text-slate-600">{label}</Label>
    <div className="flex gap-2">
      <Input id={id} value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} className="h-10" data-testid={`input-${id}`} {...props} />
      <Button type="button" size="icon" variant="outline" onClick={onAdd} aria-label={label} data-testid={`btn-${id}`}><Plus className="h-4 w-4" /></Button>
    </div>
  </div>
);

const RemoveBtn = ({ onClick, label, testId }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testId}><X className="h-3 w-3 text-slate-400 hover:text-red-500" /></button>
);

function PriceGrid({ t, values, onCell, removeOption, removeQty, testPrefix, testId, isDisabled }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr>
            <th className="p-1 text-left text-xs font-semibold text-slate-500">{t.option_label || "Opção"} / Qtd.</th>
            {t.quantities.map((q, j) => (
              <th key={q} className="p-1 text-left font-mono text-xs"><span className="inline-flex items-center gap-1">{q.toLocaleString("pt-BR")}{removeQty && <RemoveBtn onClick={() => removeQty(j)} label={`Remover quantidade ${q}`} testId={`btn-remove-qty-${j}`} />}</span></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.options.map((o, i) => (
            <tr key={o}>
              <td className="p-1 font-semibold"><span className="inline-flex items-center gap-1 whitespace-nowrap">{o}{removeOption && <RemoveBtn onClick={() => removeOption(i)} label={`Remover opção ${o}`} testId={`btn-remove-option-${i}`} />}</span></td>
              {t.quantities.map((q, j) => (
                <td key={q} className="p-1"><Input type="number" step="0.01" min="0.01" placeholder="—" disabled={isDisabled?.(i, j)} value={values[i][j]} onChange={(e) => onCell(i, j, e.target.value)} className="h-9 min-w-[90px] bg-white font-mono" data-testid={`${testPrefix}-${i}-${j}`} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResellerPrices({ t, setCell, clear }) {
  const [open, setOpen] = useState(() => t.reseller_prices.flat().some((v) => v !== ""));
  const toggle = (v) => { setOpen(v); if (!v) clear(); };
  return (
    <div className="space-y-3 rounded-md border border-violet-200 bg-violet-50/70 p-3" data-testid="reseller-prices-box">
      <div className="flex items-start gap-3">
        <Switch checked={open} onCheckedChange={toggle} data-testid="switch-reseller-prices" />
        <div>
          <p className="text-sm font-semibold text-violet-900">Preço de revenda por pacote (opcional)</p>
          <p className="text-xs text-slate-600">Preencha só os pacotes com preço especial para revendedores. Pacote vazio segue os 50% da coluna Revendedor (se marcada) ou o preço normal.</p>
        </div>
      </div>
      {open && <PriceGrid t={t} values={t.reseller_prices} onCell={setCell} testPrefix="input-table-reseller-price" testId="reseller-price-grid" isDisabled={(i, j) => t.prices[i][j] === ""} />}
    </div>
  );
}

export function PriceTableEditor({ table: t, onChange }) {
  const [opt, setOpt] = useState("");
  const [qty, setQty] = useState("");
  const both = (fn) => ({ ...t, prices: fn(t.prices), reseller_prices: fn(t.reseller_prices) });
  const setReseller = (o, q, v) => onChange({ ...t, reseller_prices: put(t.reseller_prices, o, q, v) });

  const addOption = () => {
    const name = opt.trim();
    if (!name || t.options.includes(name)) return;
    onChange({ ...both((m) => [...m, t.quantities.map(() => "")]), options: [...t.options, name] });
    setOpt("");
  };
  const addQty = () => {
    const q = parseInt(qty, 10);
    if (!(q > 0) || t.quantities.includes(q)) return;
    const at = t.quantities.filter((x) => x < q).length;
    const ins = (arr, v) => [...arr.slice(0, at), v, ...arr.slice(at)];
    onChange({ ...both((m) => m.map((r) => ins(r, ""))), quantities: ins(t.quantities, q) });
    setQty("");
  };
  const removeOption = (i) => onChange({ ...both((m) => m.filter((_, j) => j !== i)), options: t.options.filter((_, j) => j !== i) });
  const removeQty = (i) => onChange({ ...both((m) => m.map((r) => r.filter((_, j) => j !== i))), quantities: t.quantities.filter((_, j) => j !== i) });
  const setPrice = (o, q, v) => onChange({ ...t, prices: put(t.prices, o, q, v), reseller_prices: v === "" ? put(t.reseller_prices, o, q, "") : t.reseller_prices });
  const ready = t.options.length > 0 && t.quantities.length > 0;

  return (
    <div className="space-y-4 rounded-md border border-sky-200 bg-sky-50/60 p-4 sm:col-span-2" data-testid="price-table-editor">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="table-option-label" className="text-xs font-semibold text-slate-600">Nome das opções</Label>
          <Input id="table-option-label" value={t.option_label} onChange={(e) => onChange({ ...t, option_label: e.target.value })} placeholder="Ex.: Cores" className="h-10" data-testid="input-table-option-label" />
        </div>
        <Adder id="table-add-option" label="Adicionar opção" placeholder="Ex.: 4x4" maxLength={40} value={opt} setValue={setOpt} onAdd={addOption} />
        <Adder id="table-add-qty" label="Adicionar quantidade" placeholder="Ex.: 1000" type="number" min="1" value={qty} setValue={setQty} onAdd={addQty} />
      </div>
      {!ready ? (
        <p className="text-sm text-slate-500" data-testid="table-empty-hint">Adicione pelo menos uma opção e uma quantidade para preencher os preços.</p>
      ) : (
        <>
          <PriceGrid t={t} values={t.prices} onCell={setPrice} removeOption={removeOption} removeQty={removeQty} testPrefix="input-table-price" testId="price-table-grid" />
          <p className="text-xs text-slate-500">Valor fechado do pacote (R$). Deixe vazio o que você não vende.</p>
          <ResellerPrices t={t} setCell={setReseller} clear={() => onChange({ ...t, reseller_prices: t.reseller_prices.map((r) => r.map(() => "")) })} />
        </>
      )}
    </div>
  );
}
